import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useData } from '../context/DataContext';
import { MapPin, ExternalLink, Phone, Flag, Ruler, Trophy, Globe, Navigation, Eye, ChevronDown } from 'lucide-react';
import { QuickViewModal } from './QuickViewModal';
import { trackEvent } from '../lib/analytics';
import { FavoriteButton } from './FavoriteButton';
import { CardSkeleton } from './CardSkeleton';
import { useIsMobile } from '../hooks/useIsMobile';

export const CourseCard = ({ course, language, t, onQuickView }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  return (
  <div
    className={`flip-card ${isFlipped ? 'flipped' : ''}`}
    data-testid={`course-card-${course.id}`}
    onClick={(e) => {
      if (e.target.closest('a') || e.target.closest('button') || e.target.closest('.location-link')) return;
      setIsFlipped(!isFlipped);
    }}
  >
    <div className="flip-card-inner">
      {/* Front of Card */}
      <div className="flip-card-front bg-white border border-stone-100 shadow-sm rounded-2xl flex flex-col">
        {/* Image - Properly framed with center focus */}
        <div className="aspect-[4/3] flex-shrink-0 overflow-hidden rounded-t-2xl relative m-3 mb-0">
          <img
            src={course.image}
            alt={course.name}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover object-center transition-transform duration-500 rounded-xl"
            onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=800&h=600&fit=crop'; }}
          />
          {/* Price Badge */}
          {course.price_from && (
            <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur-sm text-stone-800 text-sm font-semibold px-3 py-1.5 rounded-full shadow-sm">
              From €{course.price_from}
            </div>
          )}
          {/* Quick View Button */}
          {onQuickView && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onQuickView(course);
              }}
              className="absolute top-3 right-3 w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-stone-600 hover:text-stone-900 hover:bg-white transition-all shadow-sm"
              title="Quick View"
              data-testid={`course-quick-view-${course.id}`}
            >
              <Eye className="w-4 h-4" />
            </button>
          )}
          {/* Favorite Button */}
          <FavoriteButton 
            item={{ id: course.id, type: 'golf', name: course.name, image: course.image, location: course.location, price_from: course.price_from }}
            className="absolute top-3 left-3"
          />
        </div>

        {/* Content */}
        <div className="p-5 pt-4 flex flex-col flex-1">
          <div 
            className="location-link flex items-center gap-2 text-stone-400 text-xs mb-2 cursor-pointer hover:text-brand-slate transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(course.name + ', ' + course.location + ', Mallorca')}`, '_blank');
            }}
            title="Open in Google Maps"
          >
            <MapPin className="w-3.5 h-3.5" />
            <span>{course.location}</span>
          </div>

          <Link to={`/golf-courses/${course.id}`} className="hover:text-brand-slate transition-colors">
            <h3 className="font-heading text-xl text-stone-900 mb-2">
              {course.name}
            </h3>
          </Link>

          <p className="text-stone-500 text-sm mb-4 line-clamp-2">
            {course.description[language] || course.description.en}
          </p>

          {/* Spacer pushes stats + hover hint to the bottom for layout consistency */}
          <div className="mt-auto">
            {/* Stats - inline like Hotel pricing */}
            <div className="flex items-baseline gap-2 mb-3">
              <span className="text-xs uppercase tracking-wider text-stone-400">{t('courses.holes')}</span>
              <span className="text-xl font-semibold text-stone-800">{course.holes}</span>
              <span className="text-stone-300 mx-1">|</span>
              <span className="text-xs uppercase tracking-wider text-stone-400">{t('courses.par')}</span>
              <span className="text-xl font-semibold text-stone-800">{course.par}</span>
            </div>

            {/* Hover hint (desktop) / Tap hint (móvil) */}
            <p className="text-xs text-stone-400 italic">
              <span className="hidden md:inline">Hover for details →</span>
              <span className="md:hidden">Tap for details →</span>
            </p>
          </div>
        </div>
      </div>

      {/* Back of Card */}
      <div className="flip-card-back rounded-2xl p-6 text-white" style={{ background: 'linear-gradient(135deg, #2D2D2D 0%, #3D3D3D 100%)' }}>
        <h3 className="font-heading text-2xl mb-5">{course.name}</h3>
        
        <div className="space-y-3">
          {/* Location - Clickable */}
          <div 
            className="location-link flex items-start gap-3 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(course.full_address || course.name + ', ' + course.location + ', Mallorca')}`, '_blank');
            }}
            title="Open in Google Maps"
          >
            <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Navigation className="w-4 h-4" />
            </div>
            <div>
              <p className="text-white/70 text-xs uppercase tracking-wider mb-0.5">Location</p>
              <p className="text-sm">{course.location}</p>
            </div>
          </div>

          {/* Course Info */}
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Flag className="w-4 h-4" />
            </div>
            <div>
              <p className="text-white/70 text-xs uppercase tracking-wider mb-0.5">Course</p>
              <p className="text-sm">{course.holes} Holes • Par {course.par}</p>
            </div>
          </div>

          {/* Phone if available */}
          {course.phone && (
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Phone className="w-4 h-4" />
              </div>
              <div>
                <p className="text-white/70 text-xs uppercase tracking-wider mb-0.5">Pro Shop</p>
                <p className="text-sm">{course.phone}</p>
              </div>
            </div>
          )}

          {/* Features */}
          {course.features && course.features.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {course.features.slice(0, 3).map((feature) => (
                <span
                  key={feature}
                  className="text-xs px-2 py-1 bg-white/15 rounded-full"
                >
                  {feature}
                </span>
              ))}
            </div>
          )}

          {/* Price */}
          {course.price_from && (
            <div className="bg-white/10 rounded-lg p-3 mt-3">
              <p className="text-sm font-medium flex items-center gap-2">
                <Trophy className="w-4 h-4" />
                Green Fee from €{course.price_from}
              </p>
            </div>
          )}
        </div>

        {/* CTA Button */}
        <div className="flex flex-col gap-2 mt-5">
          <a
            href={course.booking_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent('course_booking_click', { course: course.name, course_id: course.id });
              trackEvent('book_tee_time_click', { location: 'course_card', destination: 'greenfee365_course', course_name: course.name });
            }}
            className="inline-flex items-center justify-center gap-1.5 bg-white text-stone-800 px-4 py-2 rounded-full text-xs font-semibold hover:bg-white/90 transition-all"
            data-testid={`course-book-${course.id}`}
          >
            Book a Tee Time now!
            <ExternalLink className="w-3 h-3" />
          </a>
          <Link
            to={`/golf-courses/${course.id}`}
            className="inline-flex items-center justify-center gap-1.5 bg-white/15 text-white px-4 py-2 rounded-full text-xs font-medium hover:bg-white/25 transition-all border border-white/30"
            data-testid={`course-details-${course.id}`}
          >
            View Details
          </Link>
        </div>
      </div>
    </div>
  </div>
  );
};

export const GolfCourses = () => {
  const { language, t } = useLanguage();
  const { golfCourses, loading, getDisplayedItems } = useData();
  const [quickViewItem, setQuickViewItem] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const isMobile = useIsMobile();
  const MOBILE_LIMIT = 6;

  // Apply display limit
  const displayedCourses = getDisplayedItems(golfCourses, 'golf');
  const visibleCourses = (isMobile && !expanded && displayedCourses.length > MOBILE_LIMIT)
    ? displayedCourses.slice(0, MOBILE_LIMIT)
    : displayedCourses;
  const hasMore = isMobile && !expanded && displayedCourses.length > MOBILE_LIMIT;

  if (loading) {
    return (
      <section id="courses" className="section-padding bg-brand-cream">
        <div className="container-custom">
          <div className="text-center mb-8">
            <div className="h-4 bg-stone-200 rounded w-40 mx-auto mb-4 animate-pulse" />
            <div className="h-10 bg-stone-200 rounded w-72 mx-auto mb-4 animate-pulse" />
            <div className="h-4 bg-stone-200 rounded w-96 mx-auto animate-pulse" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(isMobile ? 3 : 6)].map((_, i) => <CardSkeleton key={`golf-skeleton-${i}`} />)}
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section id="courses" className="section-padding bg-brand-cream" data-testid="courses-section">
        <div className="container-custom">
          {/* Header */}
          <div className="text-center mb-8">
            <p className="text-brand-slate text-sm uppercase tracking-[0.2em] mb-4" data-testid="courses-subtitle">
              {t('courses.subtitle')}
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-stone-900 mb-4" data-testid="courses-title">
              {t('courses.title')}
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              {t('courses.description')}
            </p>
          </div>

          {/* Grid - 3 cards per row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {visibleCourses.map((course) => (
              <CourseCard 
                key={course.id} 
                course={course} 
                language={language} 
                t={t}
                onQuickView={setQuickViewItem}
              />
          ))}
        </div>

          {/* View All button - mobile only */}
          {hasMore && (
            <div className="text-center mt-8">
              <button
                onClick={() => setExpanded(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-stone-200 rounded-full text-sm font-medium text-stone-600 hover:border-stone-400 hover:text-stone-800 transition-colors shadow-sm"
                data-testid="courses-view-all"
              >
                View all {displayedCourses.length} courses
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          )}

        {/* Worldwide Booking CTA */}
        <div 
          className="mt-8 relative overflow-hidden rounded-3xl"
          data-testid="worldwide-golf-cta"
        >
          {/* Background with gradient overlay - same as hero */}
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://customer-assets.emergentagent.com/job_422d7e07-6f75-490f-aee9-3e5fef6c152e/artifacts/42pl3jtx_golf%20son-gual%20mallorca%20portada%20hole13-%20copy.JPG')`,
            }}
          />
          <div className="absolute inset-0 bg-brand-charcoal/85 md:bg-gradient-to-r md:from-brand-charcoal/95 md:via-brand-charcoal/85 md:to-brand-charcoal/70" />
          
          {/* Content */}
          <div className="relative z-10 px-5 py-10 md:px-16 md:py-16 flex flex-col md:flex-row items-center justify-between gap-6 md:gap-8">
            <div className="text-center md:text-left max-w-2xl">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-full mb-4">
                <Globe className="w-4 h-4 text-brand-gold" />
                <span className="text-white/90 text-xs sm:text-sm font-medium">Worldwide Tee Times</span>
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl md:text-4xl text-white mb-3 md:mb-4">
                {language === 'de' ? 'Golf spielen, wo immer Sie sind' :
                 language === 'fr' ? 'Jouez au golf où que vous soyez' :
                 language === 'se' ? 'Spela golf var du än är' :
                 'Play Golf Wherever You Are'}
              </h3>
              <p className="text-white/80 text-sm sm:text-base md:text-lg leading-relaxed">
                {language === 'de' ? 'Planen Sie Ihre Reise? Buchen Sie Abschlagszeiten in Ihrem Heimatland, Ihrer Zielstadt oder fast überall auf der Welt – bevor oder während Ihrer Reise.' :
                 language === 'fr' ? 'Vous planifiez votre voyage ? Réservez des départs dans votre pays, votre ville de destination ou presque partout dans le monde – avant ou pendant votre voyage.' :
                 language === 'se' ? 'Planerar du din resa? Boka starttider i ditt hemland, din destinationsstad eller nästan var som helst i världen – före eller under din resa.' :
                 'Planning your trip? Book tee times in your home country, destination city, or almost anywhere in the world – before or during your travels.'}
              </p>
            </div>
            
            <div className="flex flex-col items-center gap-3 w-full md:w-auto">
              <a
                href="https://golfinmallorca.greenfee365.com/es-ES/search?query=all_clubs"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('book_tee_time_click', { location: 'worldwide_banner', destination: 'greenfee365_worldwide' })}
                className="group inline-flex items-center justify-center gap-2 bg-white text-brand-charcoal px-5 py-3 rounded-full font-semibold text-xs sm:text-sm hover:bg-stone-100 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 w-full md:w-auto"
                data-testid="worldwide-golf-btn"
              >
                <span>
                  {language === 'de' ? 'Kurse erkunden & weltweit buchen' :
                   language === 'fr' ? 'Explorer & réserver dans le monde entier' :
                   language === 'se' ? 'Utforska & boka starttider världen över' :
                   'Explore Courses & Book Tee Times Worldwide'}
                </span>
                <ExternalLink className="w-4 h-4 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
              </a>
              <span className="text-white/60 text-xs sm:text-sm">
                {language === 'de' ? 'Über 3.000 Golfplätze verfügbar' :
                 language === 'fr' ? 'Plus de 3 000 parcours disponibles' :
                 language === 'se' ? 'Över 3 000 banor tillgängliga' :
                 'Over 3,000 courses available'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Quick View Modal */}
    <QuickViewModal
      isOpen={!!quickViewItem}
      onClose={() => setQuickViewItem(null)}
      item={quickViewItem}
      type="golf"
      language={language}
      t={t}
    />
  </>
  );
};
